import React, {useContext} from 'react';

import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import {LocaleSwitcher} from '@homeaway/react-locale-switcher';
import {TrackingContext} from '@homeaway/react-event-tracking';

import GlobalizationSelectorConnector from '../../../connectors/GlobalizationSelectorConnector';
import CurrencySelectorConnector from '../../../connectors/CurrencySelectorConnector';

import {namedEvents, linkLocations} from '../../../utils/edap';
import {clickedOnTravelerLogin} from '../../../utils/login.utils';
import {loginRegex} from '../../../utils/regex';
import travelerSvgMap from '../../../utils/traveler-svgs';

import {Scratchpad} from '../../scratchpad/Scratchpad';
import {SignUp} from '../../signup/SignUp';

// eslint-disable-next-line complexity
function TrayMenu({
    countryCode,
    currencyCode,
    currencyList,
    currencySelectorVariant,
    extraNav,
    feedbackComponent,
    flags,
    globalizationSelector,
    handleTravelerLogin,
    help,
    isMobile,
    isOpen,
    languageCode,
    localeSwitcher,
    login,
    lyp,
    onGlobalSelectorChange,
    renderCountryLanguageSelector,
    renderCurrencySelector,
    scratchpad,
    setActiveMenu,
    signUp,
    traveler
}) {
    // Add the context to be used for pushing edap events
    const context = useContext(TrackingContext);

    function handleClickHeaderFlag() {
        setActiveMenu('flag');
    }

    function handleClickHelp() {
        setActiveMenu('help');
    }

    function handleClickLocaleSwitcher() {
        setActiveMenu('locale-switcher');
    }

    function handleClickCurrencySelector() {
        setActiveMenu('top-currency-menu');

        let topDisplayCurrencies = currencyList;
        let topDisplayCurrenciesCode = topDisplayCurrencies.map((currency) => currency.code);
        topDisplayCurrenciesCode = topDisplayCurrenciesCode.join(',');

        context.trigger(namedEvents.CURRENCY_SELECTOR_SELECTED, {
            action_location: linkLocations.SIDEBAR_CURRENCY_SELECTOR,
            displaycurrency: currencyCode
        });

        context.trigger(namedEvents.CURRENCY_SET_PRESENTED, {
            action_location: linkLocations.SIDEBAR_TOP_CURRENCY_LIST,
            displaycurrency: currencyCode,
            set_type: 'TOP',
            topdisplaycurrencies: topDisplayCurrenciesCode
        });
    }

    function handleClickMenuItem(item) {
        return () => {
            /* istanbul ignore else */
            if (item.cssClass === 'se-traveler-trips') {
                context.trigger(namedEvents.TRIP_COLLECTION_SELECTED, {
                    action_location: 'header'
                });
            }
        };
    }

    function handleClickLogin(item) {
        return (event) => {
            if (login.travelerLoginClickHandler && clickedOnTravelerLogin(item.cssClass)) {
                event.stopPropagation();
                event.preventDefault();
                handleTravelerLogin();
                login.travelerLoginClickHandler();
                return;
            }

            // CAS Login URLs need to be double encoded because of how their params are structured:
            // https://{DOMAIN}/auth/traveler/login?service=https://{DOMAIN}/exp/sso/auth?lt=traveler&context=def&service={DOUBLE_ENCODED_RETURN_URL}&kmsi=true
            let doubleEncodedURI = encodeURIComponent(encodeURIComponent(window.location.pathname + window.location.search));

            let loginRE = loginRegex.LOGIN_RE;
            // if login, perform specific link update
            let newLink = item.link.replace(
                loginRE,
                `$1${doubleEncodedURI}&kmsi=true`
            );

            // if we generate a new link, user has navigated to a sub URL in the same application
            // and we should redirect them there.  Otherwise let default user-agent behavior handle
            // rendered link
            /* istanbul ignore else */
            if (newLink !== item.link) {
                event.stopPropagation();
                event.preventDefault();
                window.location.assign(newLink);
            }
        };
    }

    return (
        <div className="site-header-tray__menu">
            <div className="site-header-tray__menu-items">
                {scratchpad && (
                    <a className="site-header-tray__menu-item-link" data-bypass="true" href={scratchpad.link}>
                        <Scratchpad
                            iconSize="sm"
                            label={scratchpad.label}
                            link={scratchpad.link}
                        />
                    </a>
                )}

                {traveler && traveler.items.map((item, index) => {
                    if (item.cssClass === 'divider') {
                        return (
                            <div className="site-header-divider" key={`divider-${index}`} />
                        );
                    }

                    // FIXME: This is a hack to identify the inbox item
                    // TODO: Sort out a maintainable way to indentify and handle inbox and inbox data
                    if (item.cssClass === 'se-traveler-inbox') {
                        return (
                            <a className="site-header-tray__menu-item-link" data-bypass="true" href={item.link} key={item.link || index}>
                                <SVGIcon inlineFlex markup={travelerSvgMap[item.icon]}/><span className="site-header-tray__menu-item-text">{item.text}</span>
                            </a>
                        );
                    }

                    return (
                        <a className="site-header-tray__menu-item-link" data-bypass="true" onClick={handleClickMenuItem(item)} href={item.link} key={item.link || index}>
                            <SVGIcon inlineFlex markup={travelerSvgMap[item.icon]}/><span className="site-header-tray__menu-item-text">{item.text}</span>
                        </a>
                    );
                })}

                {login && login.items && login.items.map((item, index) =>
                    <a className="site-header-tray__menu-item-link" data-bypass="true" href={item.link} key={item.link || index} onClick={handleClickLogin(item)}>{item.text}</a>
                )}

                {signUp && login && (
                    <SignUp {...login} {...signUp}/>
                )}

                {help && (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                        className="site-header-tray__menu-item-link site-header-tray__menu-item-link--icon-right"
                        data-bypass="true"
                        onClick={handleClickHelp}
                        onKeyPress={handleClickHelp}
                        role="button"
                        tabIndex={0}
                    >
                        <span>{help.label}</span>
                        <SVGIcon className="text-muted" inline markup={CHEVRON_RIGHT__16} />
                    </a>
                )}

                {extraNav && extraNav.items && extraNav.items.map((item, index) =>
                    <a className="site-header-tray__menu-item-link" data-bypass="true" href={item.link} key={index}>{item.text}</a>
                )}

                <div className="feedback-container">
                    {feedbackComponent && (
                        <div className="site-header-tray__menu-item-link--feedback">{feedbackComponent}</div>
                    )}
                </div>

                <div className="currency-selector-container">
                    {renderCurrencySelector
                    && (currencySelectorVariant === 2)
                    && (
                        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
                        <a className="site-header-tray__menu-item-link"
                            onClick={handleClickCurrencySelector}
                            onKeyPress={handleClickCurrencySelector}
                            id="currency-selector-menu"
                        >
                            <CurrencySelectorConnector
                                {...globalizationSelector}
                                currencyCode={currencyCode}
                                onCurrencySelectorChange={onGlobalSelectorChange}
                                renderCurrencySelector={renderCurrencySelector}
                                isMobile={isMobile}
                            />
                        </a>
                    )}
                </div>

                {localeSwitcher && (
                    <div
                        className="site-header-tray__menu-item-link"
                        onClick={handleClickLocaleSwitcher}
                        onKeyPress={handleClickLocaleSwitcher}
                        role="button"
                        tabIndex={0}
                        id="locale-switcher-menu"
                    >
                        <LocaleSwitcher/>
                    </div>
                )}

                {flags && (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                        className="site-header-tray__menu-item-link site-header-tray__menu-item-link--flag site-header-tray__menu-item-link--icon-right"
                        data-bypass="true"
                        onClick={handleClickHeaderFlag}
                        onKeyPress={handleClickHeaderFlag}
                        role="button"
                        tabIndex={0}
                    >
                        <span>
                            {isOpen && <i aria-label="flags" className={`flag-${flags.items[0].countryCode}`} />}
                            <span>{flags.items[0].text}</span>
                        </span>
                        <SVGIcon className="text-muted" inline markup={CHEVRON_RIGHT__16} />
                    </a>
                )}

                {lyp && (
                    <a className="site-header-tray__menu-item-link" data-bypass="true" href={lyp.link} target="_blank">{lyp.text}</a>
                )}

                <div className="currency-selector-container">
                    {(renderCountryLanguageSelector || renderCurrencySelector)
                    && (currencySelectorVariant === 1)
                    && (
                        <div className="site-header-tray__menu-item-globalization-selector">
                            <GlobalizationSelectorConnector
                                {...globalizationSelector}
                                countryCode={countryCode}
                                currencyCode={currencyCode}
                                languageCode={languageCode}
                                onGlobalSelectorChange={onGlobalSelectorChange}
                                renderCountryLanguageSelector={renderCountryLanguageSelector}
                                renderCurrencySelector={renderCurrencySelector}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export {TrayMenu};
